import { FC, useState, MouseEvent } from 'react';
import { Box, AppBar, Toolbar, IconButton, Menu, MenuItem, Divider } from '@mui/material';
import { Menu as MenuIcon, AccountCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import * as CookieConsent from 'vanilla-cookieconsent';
import { Copy } from '../elements/Copy';
import { NavItem } from '../widgets';
import { ROUTE, secondaryNavItems } from '../../constants/general';
import { useUserAtom } from '../../atoms';
import { logout } from '../../services';
import { NavItemProps } from '../../@types/general';
import { leadIdAtom } from '../../atoms/leadAtom';

export type PrimaryNavProps = object;

export const PrimaryNav: FC<PrimaryNavProps> = () => {
  const { user, logout: userReset } = useUserAtom();
  const [leadId] = useAtom(leadIdAtom);
  const navigate = useNavigate();
  const isLoggedIn = !!user.userId;

  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const handleOpenNavMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const navItems: NavItemProps[] = [
    !isLoggedIn && { label: 'primaryNav.signin', href: ROUTE.signin },
  ].filter(Boolean) as NavItemProps[];

  const userMenuItems = [
    {
      label: 'primaryNav.cookiePreferences',
      onClick: () => {
        CookieConsent.showPreferences();
        handleCloseUserMenu();
      },
    },
    ...(isLoggedIn ? [{
      label: 'primaryNav.logout',
      onClick: () => {
        logout().then(() => {
          userReset();
          navigate(ROUTE.signin);
        });
        handleCloseUserMenu();
      },
    }] : []),
  ];

  const secondaryEnhancedNavItems = secondaryNavItems.map(i => ({
    label: i.label,
    href: `${ROUTE.leads}/${leadId}/${i.href}`,
  }));

  return (
    <Box className="RW-PrimaryNav" sx={{ mb: 0.1 }}>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: 'common.white',
        }}
      >
        <Toolbar sx={{ pt: 2, pb: 1, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', flexDirection: 'row', width: { xs: 'auto', md: '40%' } }}>
            <Box
              component="img"
              src="/images/renoworks-color-logo.png"
              sx={{ width: { xs: '120px', sm: '180px', md: '100%' }, maxWidth: '259px', mr: 2 }}
              display="inline-block"
            />
            <Copy
              variant="h4"
              locale="primaryNav.brand"
              sx={{
                color: 'secondary.main',
                flexGrow: 1,
                fontWeight: 300,
                verticalAlign: 'bottom',
                display: 'inline',
                whiteSpace: 'nowrap',
              }}
            />
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          {/* Mobile Menu */}
          <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
            <IconButton
              aria-haspopup="true"
              aria-controls="primarynav-mobile-menu"
              onClick={handleOpenNavMenu}
            >
              <MenuIcon color="primary" />
            </IconButton>
            <Menu
              id="primarynav-mobile-menu"
              open={Boolean(anchorElNav)}
              anchorEl={anchorElNav}
              keepMounted
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              onClose={handleCloseNavMenu}
              PaperProps={{
                sx: {
                  maxHeight: '70vh',
                  overflow: 'auto',
                  mt: 1,
                }
              }}
            >
              {navItems.map(navItemProps => (
                <MenuItem key={navItemProps.label}>
                  <NavItem
                    {...navItemProps}
                    onClick={e => {
                      navItemProps.onClick?.(e);
                      handleCloseNavMenu();
                    }}
                  />
                </MenuItem>
              ))}
              {isLoggedIn && leadId && secondaryEnhancedNavItems.map(navItemProps => (
                <MenuItem key={navItemProps.label} onClick={handleCloseNavMenu}>
                  <NavItem {...navItemProps} />
                </MenuItem>
              ))}
              {isLoggedIn && leadId && <Divider />}
              {userMenuItems.map((item) => (
                <MenuItem key={item.label} onClick={() => {
                  item.onClick();
                  handleCloseNavMenu();
                }}>
                  <Copy locale={item.label} />
                </MenuItem>
              ))}
            </Menu>
          </Box>
          {/* Desktop */}
          <Box sx={{ display: { xs: 'none', lg: 'flex' }, gap: 2, alignItems: 'center' }}>
            {navItems.map(navItemProps => (
              <NavItem key={navItemProps.label} {...navItemProps} />
            ))}
            <IconButton
              onClick={handleOpenUserMenu}
              color="primary"
              aria-label="user menu"
              aria-controls="user-menu"
              aria-haspopup="true"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="user-menu"
              anchorEl={anchorElUser}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                elevation: 3,
                sx: {
                  mt: 1.5,
                  minWidth: 220,
                }
              }}
            >
              {userMenuItems.map((item) => (
                <MenuItem
                  key={item.label}
                  onClick={item.onClick}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  <Copy locale={item.label} />
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
};
